import {
  Component,
  computed,
  inject,
  input,
  model,
  output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TRANSLOCO_SCOPE, TranslocoModule, TranslocoService } from '@jsverse/transloco';
import {
  QaiButtonComponent,
  QaiCheckboxComponent,
  QaiFieldComponent,
  QaiFloatingDirective,
  QaiFileUploadComponent,
  QaiIconButtonComponent,
  QaiIconComponent,
  QaiInputComponent,
  QaiFloatingSelectComponent,
  QaiMenuItemButtonComponent,
  QaiMultiSelectComponent,
  QaiTextareaComponent,
  QaiTooltipComponent,
  getFileKey,
  type QaiMultiSelectOption,
} from '@qai/ui';
import { SIMULATION_NAME_ALREADY_EXISTS_STATUS, AuthorService } from 'src/app/services/self-serve/author.service';
import { IModule } from 'src/app/interfaces/self-serve/responses/imodule';
import { IConfirmActionPayload } from 'src/app/interfaces/self-serve/requests/iconfirm-action-payload';
import { ISelfAuthoringPrefillData } from 'src/app/interfaces/self-serve/iself-authoring-prefill-data';
import { SelfServeLocalStorageUtil } from 'src/app/utils/local-storage.util';
import { IDocumentUploadState } from 'src/app/interfaces/self-serve/idocument-upload-state';
import { AmplitudeService } from 'src/app/services/external-tools/amplitude.service';
import { AmplitudeTrackingEvents } from 'src/app/utils/enums/amplitude/amplitudeTrackingEvents.enum';
import { SimulationTypeEnum } from 'src/app/utils/enums/self-serve/simulation-type.enum';
import { TagService } from 'src/app/services/self-serve/tag.service';
import { LanguageService } from 'src/app/services/language.service';

interface UploadBatch {
  files: File[];
  settled: number;
}

@Component({
  selector: 'self-authoring-simulation-form',
  standalone: true,
  templateUrl: './self-authoring-simulation-form.component.html',
  styleUrls: ['./self-authoring-simulation-form.component.scss'],
  imports: [
    FormsModule,
    QaiFieldComponent,
    QaiButtonComponent,
    QaiInputComponent,
    QaiFloatingSelectComponent,
    QaiTextareaComponent,
    QaiFileUploadComponent,
    QaiMultiSelectComponent,
    QaiMenuItemButtonComponent,
    QaiIconComponent,
    QaiIconButtonComponent,
    QaiCheckboxComponent,
    QaiTooltipComponent,
    QaiFloatingDirective,
    TranslocoModule,
  ],
  providers: [{ provide: TRANSLOCO_SCOPE, useValue: 'simulationBuilder' }],
})
export class SelfAuthoringSimulationFormComponent {
  private translocoService = inject(TranslocoService);
  private amplitudeService = inject(AmplitudeService);
  private tagService = inject(TagService);
  private languageService = inject(LanguageService);

  @ViewChild('tagsSelect') tagsSelect?: QaiMultiSelectComponent;

  open = input(false);
  networkRequestInProgress = input(false);
  errorStatus = input<number>();
  isFilesExceedingTokens = input(false);
  isSelfAuthoringRoleplayEnabled = input(false);
  prefillData = input<ISelfAuthoringPrefillData | undefined>(undefined);
  simulationType = input<SimulationTypeEnum>(SimulationTypeEnum.ROLE_PLAY);

  isKnowledgeCheck = computed(() => this.simulationType() === SimulationTypeEnum.KNOWLEDGE_CHECK);

  isSelfAuthoring = computed(() => {
    if (!this.isSelfAuthoringRoleplayEnabled()) {
      return false;
    }

    const accountUuid = SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();
    return accountUuid
      ? SelfServeLocalStorageUtil.getSelectedAccountRoles(accountUuid).hasAuthor
      : false;
  });

  isAmendDocumentsMode = computed(() => !!this.prefillData());

  confirmAction = output<IConfirmActionPayload>();
  closeModal = output<void>();
  dialogReopenRequested = output<void>();
  showLeaveConfirmation = model(false);
  isLeaving = model(false);

  inputName = '';
  showError = false;
  duplicateError = false;
  moduleOptions: { label: string; value: string }[] = [];
  selectedModuleUuid: string = '';
  isLoadingModuleOptions: boolean = false;
  scenario = '';
  generateHints = false;
  generateResourceNotes = false;
  uploadedFiles: File[] = [];
  documentUploads: IDocumentUploadState[] = [];
  fileUploadError?: string;
  tagOptions: QaiMultiSelectOption[] = [];
  selectedTagUuids: string[] = [];
  isCreatingTag = false;
  tagError?: string;

  constructor(private authorService: AuthorService) {}

  loadProducts(): void {
    const accountUuid =
      SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();

    if (!accountUuid) {
      console.error('No account UUID found for loading module options');
      this.moduleOptions = [];
      return;
    }

    this.isLoadingModuleOptions = true;
    this.authorService.getModuleList(accountUuid).subscribe({
      next: (modules: IModule[]) => {
        this.moduleOptions = modules.map((module) => ({
          label: module.name,
          value: module.uuid,
        }));
        this.isLoadingModuleOptions = false;
      },
      error: (err: any) => {
        console.error('Failed to load module options:', err);
        this.moduleOptions = [];
        this.isLoadingModuleOptions = false;
      },
    });
  }

  onAddNewProduct(moduleName: string): void {
    if (!moduleName || moduleName.trim() === '') {
      return;
    }

    this.isLoadingModuleOptions = true;
    this.authorService.createModule(moduleName.trim()).subscribe({
      next: (newModule: IModule) => {
        this.moduleOptions.push({
          label: newModule.name,
          value: newModule.uuid,
        });
        this.selectedModuleUuid = newModule.uuid;
        this.isLoadingModuleOptions = false;
      },
      error: (err: any) => {
        console.error('Failed to create new module:', err);
        this.isLoadingModuleOptions = false;
      },
    });
  }

  onModuleSelectionChange(value: string | undefined): void {
    if (value) {
      this.selectedModuleUuid = value;
    }
  }

  loadTags(): void {
    const accountUuid = SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();
    if (!accountUuid) {
      this.tagOptions = [];
      return;
    }

    this.tagService.listTagsForAccount(accountUuid).subscribe({
      next: (tags) => {
        this.tagOptions = tags.map((tag) => ({ value: tag.tagUuid, label: tag.name }));
        // qai-multiselect wipes its internal value when it receives a value before any options,
        // so hand prefilled tags back as a fresh array once the options exist (dropping deleted tags).
        const knownTagUuids = new Set(tags.map((tag) => tag.tagUuid));
        this.selectedTagUuids = this.selectedTagUuids.filter((uuid) => knownTagUuids.has(uuid));
      },
      error: (err: any) => {
        console.error('Failed to load tags:', err);
        this.tagOptions = [];
      },
    });
  }

  onTagsChange(values: string[]): void {
    if (this.isAmendDocumentsMode()) {
      this.selectedTagUuids = [...this.selectedTagUuids];
      return;
    }
    this.selectedTagUuids = values;
    this.tagError = undefined;
    this.tagsSelect?.clearSearch();
  }

  // Tag labels are user-supplied, so casing is folded locale-aware (Turkish dotted/dotless I).
  /** True when the typed term is not an exact (case-insensitive) match for a known tag. */
  canCreateTag(term: string): boolean {
    const trimmed = term.trim();
    if (!trimmed) {
      return false;
    }
    const locale = this.languageService.getActiveLanguage();
    return !this.tagOptions.some((o) => o.label.toLocaleLowerCase(locale) === trimmed.toLocaleLowerCase(locale));
  }

  onCreateTag(term: string): void {
    const name = term.trim();
    const accountUuid = SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();
    if (!name || !accountUuid || this.isCreatingTag) {
      return;
    }

    this.isCreatingTag = true;
    this.tagError = undefined;

    this.tagService.createTag(accountUuid, name).subscribe({
      next: ({ tagUuid }) => {
        this.isCreatingTag = false;
        this.tagOptions = [{ value: tagUuid, label: name }, ...this.tagOptions];
        this.selectedTagUuids = [...this.selectedTagUuids, tagUuid];
        this.tagsSelect?.clearSearch();
      },
      error: () => {
        this.isCreatingTag = false;
        this.tagError = this.translocoService.translate('simulationBuilder.TAG_CREATE_ERROR');
        this.tagsSelect?.clearSearch();
        this.tagsSelect?.closeDropdown();
      },
    });
  }

  onFilesSelected(files: File[]): void {
    this.fileUploadError = undefined;
    this.uploadedFiles = files;
    const newFiles = files.filter((file) => !this.documentUploads.some((doc) => doc.file === file));
    if (newFiles.length === 0) {
      return;
    }

    const batch: UploadBatch = { files: newFiles, settled: 0 };
    newFiles.forEach((file) => this.uploadDocument(file, batch));
  }

  onFileRemoveRequested(file: File): void {
    const doc = this.documentUploads.find((d) => d.file === file);

    if (!doc || doc.status === 'uploading' || doc.status === 'deleting') {
      return;
    }

    if (doc.status === 'error' || !doc.customerContentUuid) {
      this.documentUploads = this.documentUploads.filter((d) => d !== doc);
      this.uploadedFiles = this.uploadedFiles.filter((f) => f !== file);
      this.clearDocumentDependentFieldsIfNoDocuments();
      return;
    }

    const accountUuid = SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();
    if (!accountUuid) {
      return;
    }

    const customerContentUuid = doc.customerContentUuid;
    doc.status = 'deleting';

    doc.subscription = this.authorService
      .deleteCustomerContent(accountUuid, { customerContentUuids: [customerContentUuid] })
      .subscribe({
        next: () => {
          this.documentUploads = this.documentUploads.filter((d) => d !== doc);
          this.uploadedFiles = this.uploadedFiles.filter((f) => f !== file);
          this.clearDocumentDependentFieldsIfNoDocuments();
        },
        error: () => {
          doc.status = 'success';
          this.fileUploadError = this.translocoService.translate('simulationBuilder.REFERENCE_DOCUMENT_DELETE_ERROR');
        },
      });
  }

  private uploadDocument(file: File, batch: UploadBatch): void {
    const accountUuid = SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();
    if (!accountUuid) {
      this.settleBatch(batch);
      return;
    }

    const doc: IDocumentUploadState = { file, status: 'uploading' };
    this.documentUploads.push(doc);

    doc.subscription = this.authorService
      .uploadCustomerContent(accountUuid, file)
      .subscribe({
        next: (res) => {
          doc.status = 'success';
          doc.customerContentUuid = res.customerContentUuid;
          this.settleBatch(batch);
        },
        error: () => {
          doc.status = 'error';
          this.fileUploadError = this.translocoService.translate('simulationBuilder.REFERENCE_DOCUMENT_UPLOAD_ERROR');
          this.settleBatch(batch);
        },
      });
  }

  private settleBatch(batch: UploadBatch): void {
    batch.settled++;
    if (batch.settled < batch.files.length) {
      return;
    }

    const successfulFiles = batch.files.filter(
      (file) => this.documentUploads.find((doc) => doc.file === file)?.status === 'success'
    );
    if (successfulFiles.length === 0) {
      return;
    }

    this.amplitudeService.trackEvent(AmplitudeTrackingEvents.SELF_AUTHORING_DOCUMENT_UPLOADED, {
      file_count: successfulFiles.length,
      // SAFE: file extension normalization, not localized text
      file_types: successfulFiles.map((file) => file.name.split('.').pop()?.toLowerCase() ?? file.type),
    });
  }

  // Knowledge Check shows Instructions and generation options only once a document has uploaded.
  showDocumentDependentFields(): boolean {
    return (
      this.isKnowledgeCheck() &&
      this.uploadedFiles.some(
        (file) => this.documentUploads.find((doc) => doc.file === file)?.status === 'success'
      )
    );
  }

  private clearDocumentDependentFieldsIfNoDocuments(): void {
    if (!this.isKnowledgeCheck() || this.showDocumentDependentFields()) {
      return;
    }
    this.scenario = '';
    this.generateHints = false;
    this.generateResourceNotes = false;
  }

  isAnyDocumentUploading(): boolean {
    return this.documentUploads.some((doc) => doc.status === 'uploading');
  }

  fileStatuses(): Record<string, string> {
    const statuses: Record<string, string> = {};
    for (const doc of this.documentUploads) {
      const key = getFileKey(doc.file);
      if (doc.status === 'uploading') statuses[key] = this.translocoService.translate('simulationBuilder.REFERENCE_DOCUMENT_STATUS_UPLOADING');
      if (doc.status === 'deleting') statuses[key] = this.translocoService.translate('simulationBuilder.REFERENCE_DOCUMENT_STATUS_DELETING');
      if (doc.status === 'success') statuses[key] = this.translocoService.translate('simulationBuilder.REFERENCE_DOCUMENT_STATUS_SUCCESS');
      if (doc.status === 'error') statuses[key] = this.translocoService.translate('simulationBuilder.REFERENCE_DOCUMENT_STATUS_ERROR');
    }
    return statuses;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['open']) {
      const isOpenNow = changes['open']?.currentValue;

      if (isOpenNow) {
        const prefill = this.prefillData();

        this.inputName = prefill?.name ?? '';
        this.showError = !prefill && !this.isSelfAuthoring();
        this.duplicateError = false;

        this.scenario = prefill?.scenario ?? '';
        this.showLeaveConfirmation.set(false);
        this.isLeaving.set(false);

        this.selectedModuleUuid = prefill?.moduleUuid ?? '';
        this.moduleOptions = prefill?.moduleName
          ? [{ label: prefill.moduleName, value: prefill.moduleUuid ?? '' }]
          : [];
        this.loadProducts();

        this.selectedTagUuids = prefill?.tagUuids ?? [];
        this.tagError = undefined;
        this.isCreatingTag = false;
        if (this.isSelfAuthoring()) {
          this.loadTags();
        }

        if (prefill) {
          this.documentUploads = prefill.files.map((f) => ({
            file: new File([new Uint8Array(f.size ?? 0)], f.name),
            status: 'success',
            customerContentUuid: f.customerContentUuid,
            isPreExisting: true,
          }));
          this.uploadedFiles = this.documentUploads.map((doc) => doc.file);
        } else {
          this.uploadedFiles = [];
        }

        if (this.isSelfAuthoring() && !prefill) {
          this.amplitudeService.trackEvent(AmplitudeTrackingEvents.SELF_AUTHORING_STARTED, {
            user_role: 'author',
          });
        }
      }
    }

    if (changes['errorStatus']) {
      this.handleErrorStatusChange(changes['errorStatus'].currentValue);
    }

    if (changes['isFilesExceedingTokens']?.currentValue) {
      this.fileUploadError = this.translocoService.translate('simulationBuilder.CONTENT_LIMIT_EXCEEDED_ERROR');
    }
  }

  private handleErrorStatusChange(status?: number): void {
    if (status === SIMULATION_NAME_ALREADY_EXISTS_STATUS) {
      this.duplicateError = true;
      this.showError = false;
    }
  }

  onInputChange(): void {
    this.showError = false;
    this.duplicateError = false;
  }

  handleConfirmClick() {
    if (this.networkRequestInProgress()) {
      return;
    }

    if (this.inputName.trim()) {
      this.showError = false;
      this.duplicateError = false;
      const payload: IConfirmActionPayload = {
        name: this.inputName.trim(),
      };
      if (this.selectedModuleUuid) {
        payload.moduleUuid = this.selectedModuleUuid;
        payload.moduleName = this.moduleOptions.find((option) => option.value === this.selectedModuleUuid)?.label;
      }
      if (this.isSelfAuthoring()) {
        const attachedDocs = this.uploadedFiles
          .map((file) => this.documentUploads.find((doc) => doc.file === file))
          .filter((doc): doc is IDocumentUploadState => !!doc?.customerContentUuid);
        if (!this.isKnowledgeCheck()) {
          payload.scenario = this.scenario.trim();
        } else if (attachedDocs.length) {
          payload.scenario = this.scenario.trim();
          payload.hintsVisible = this.generateHints;
          payload.referenceVisible = this.generateResourceNotes;
        }
        payload.customerContentUuids = attachedDocs.map((doc) => doc.customerContentUuid as string);
        if (this.selectedTagUuids.length) {
          payload.tagUuids = [...this.selectedTagUuids];
        }
        payload.documents = attachedDocs.map((doc) => ({
          name: doc.file.name,
          size: doc.file.size,
          customerContentUuid: doc.customerContentUuid as string,
        }));
      }
      this.confirmAction.emit(payload);
    } else {
      this.showError = true;
      this.duplicateError = false;
    }
  }

  handleCloseClick() {
    if (this.isAmendDocumentsMode()) {
      // it should just quietly clean up any newly-added (not pre-existing) uploads.
      this.onLeaveConfirmed();
      return;
    }

    if (this.hasUnsavedChanges()) {
      this.showLeaveConfirmation.set(true);
      return;
    }

    this.resetFormState();
    this.closeModal.emit();
  }

  private hasUnsavedChanges(): boolean {
    return (
      !!this.inputName.trim() ||
      !!this.selectedModuleUuid ||
      !!this.scenario.trim() ||
      this.uploadedFiles.length > 0 ||
      this.selectedTagUuids.length > 0
    );
  }

  onLeaveCancelled(): void {
    this.showLeaveConfirmation.set(false);
    this.dialogReopenRequested.emit();
  }

  onLeaveConfirmed(): void {
    const customerContentUuids = this.documentUploads
      .filter((doc) => doc.status === 'success' && doc.customerContentUuid && !doc.isPreExisting)
      .map((doc) => doc.customerContentUuid as string);

    if (customerContentUuids.length === 0) {
      this.finishLeaving();
      return;
    }

    const accountUuid = SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();
    if (!accountUuid) {
      this.finishLeaving();
      return;
    }

    this.isLeaving.set(true);

    this.authorService.deleteCustomerContent(accountUuid, { customerContentUuids }).subscribe({
      next: () => {
        this.isLeaving.set(false);
        this.finishLeaving();
      },
      error: () => {
        this.isLeaving.set(false);
      },
    });
  }

  private finishLeaving(): void {
    this.showLeaveConfirmation.set(false);
    this.resetFormState();
    this.closeModal.emit();
  }

  protected resetFormState(): void {
    this.inputName = '';
    this.scenario = '';
    this.generateHints = false;
    this.generateResourceNotes = false;
    this.uploadedFiles = [];
    this.selectedModuleUuid = '';
    this.showError = false;
    this.duplicateError = false;
    this.fileUploadError = undefined;
    this.selectedTagUuids = [];
    this.tagError = undefined;

    this.documentUploads.forEach((doc) => doc.subscription?.unsubscribe());
    this.documentUploads = [];
  }

  isConfirmDisabled(): boolean {
    if (this.networkRequestInProgress() || !this.inputName.trim()) {
      return true;
    }

    if (!this.selectedModuleUuid) {
      return true;
    }

    if (this.isSelfAuthoring()) {
      // Reference documents are optional for Knowledge Check.
      if (this.isKnowledgeCheck() && this.uploadedFiles.length === 0) {
        return false;
      }

      if (this.uploadedFiles.length === 0) {
        return true;
      }

      const isAnyFileProcessing = this.uploadedFiles.some((file) => {
        const status = this.documentUploads.find((doc) => doc.file === file)?.status;
        return status === 'uploading' || status === 'deleting';
      });
      if (isAnyFileProcessing) {
        return true;
      }

      return !this.documentUploads.some((doc) => doc.status === 'success');
    }

    return false;
  }

  confirmButtonLabel(): string {
    if (this.isSelfAuthoring()) {
      return this.translocoService.translate('simulationBuilder.CREATE_SIMULATION_DETAILS_BUTTON');
    }

    const action = this.translocoService.translate('simulationBuilder.ACTION_CREATE');
    const type = this.translocoService.translate('simulationBuilder.ENTITY_TYPE_SIMULATION_LOWER');
    return this.translocoService.translate('simulationBuilder.CONFIRM_BUTTON_LABEL', { action, type });
  }

  get shouldShowError(): boolean {
    return this.showError || this.duplicateError;
  }

  get errorMessage(): string {
    if (this.duplicateError) {
      return this.translocoService.translate('simulationBuilder.DUPLICATE_NAME_ERROR_SIMULATION');
    }
    if (this.showError) {
      return this.translocoService.translate('simulationBuilder.NAME_REQUIRED_ERROR');
    }
    return '';
  }
}
